import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  CircleCheck,
  ClipboardList,
  Cog,
  Network,
  Plug,
  TriangleAlert,
} from "lucide-react";

import { commits, integrations, planning } from "@/api";
import { useApi, useUnit } from "@/hooks";

/**
 * The persistent planning status strip under the app header: one thin line
 * that answers "is the schedule in a good state?" without opening a single
 * dialog. Every figure is read from the same endpoints the pages themselves
 * use — nothing here is computed from anything the server did not say:
 *
 *   · committed-plan meta + counts  — GET /api/commit (commits.getCommittedPlan),
 *     the exact document the Line Plan prints
 *   · last optimization             — GET /api/schedule/last
 *     (planning.getLastSchedule), read with the same
 *     `solved_at ?? generated_at` fallback the Line Plan's board-ahead
 *     banner uses
 *   · SAP / eBMR                    — GET /api/integrations/status
 *     (integrations.getIntegrationsStatus), the Integration Status page's
 *     own source. Never says "Connected": nothing here contacts a remote,
 *     so it reports what the config actually is — Simulated, Configured or
 *     Not configured (see `connector` below)
 *
 * A segment whose data is missing renders a muted "—", never a guess.
 * Everything refreshes on unit change and on a 60s tick (which also
 * re-renders the relative "X min ago").
 */

/** Icons at text size; stroke matches the nav glyphs (navigation.jsx). */
const glyph = (Glyph, color) => (
  <Glyph size={13} strokeWidth={2.25} style={color ? { color } : undefined} />
);

const Dot = ({ color }) => (
  <span
    aria-hidden="true"
    className="inline-block shrink-0 rounded-full"
    style={{ height: 7, width: 7, background: color }}
  />
);

/** "just now" / "4 min ago" / "3 h ago" / "2 d ago" — no library needed. */
function relative(iso, nowMs) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const mins = Math.round((nowMs - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/** One deep-linking segment: plain text row, hover underline, no chrome. */
function Segment({ to, title, children, className = "" }) {
  const navigate = useNavigate();
  return (
    <span
      role="link"
      tabIndex={0}
      title={title}
      className={`items-center gap-1.5 whitespace-nowrap cursor-pointer hover:underline ${className}`}
      style={{ color: "var(--text-secondary)" }}
      onClick={() => navigate(to)}
      onKeyDown={(e) => e.key === "Enter" && navigate(to)}
    >
      {children}
    </span>
  );
}

const Sep = ({ className = "" }) => (
  <span
    aria-hidden="true"
    className={`items-center ${className}`}
    style={{ color: "var(--text-muted)" }}
  >
    ·
  </span>
);

export default function PlanningStatusBar() {
  const unit = useUnit();

  // The 60s tick drives both the polling re-fetch (it sits in each useApi
  // dependency list) and the re-render of the relative timestamp.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const nowMs = Date.now(); // re-read every render; the tick forces one a minute

  const committed = useApi(() => commits.getCommittedPlan(unit), [unit, tick], {
    skip: !unit,
  });
  const lastSolve = useApi(() => planning.getLastSchedule(unit), [unit, tick], {
    skip: !unit,
  });
  const integ = useApi(
    () => integrations.getIntegrationsStatus(unit),
    [unit, tick],
    { skip: !unit },
  );

  // ---- derivations ---------------------------------------------------------
  const plan = committed.data; // CommittedPlan JSON, or null when none yet
  const committedAt = plan?.committed_at ?? null;
  // Same field fallback as LinePlanPage's board-ahead banner.
  const solvedAt = lastSolve.data?.solved_at ?? lastSolve.data?.generated_at ?? null;

  // Newer solve than the committed plan ⇒ the board holds uncommitted work.
  // Both timestamps are required to claim it — missing data never accuses.
  const uncommitted = Boolean(
    solvedAt && committedAt && new Date(solvedAt) > new Date(committedAt),
  );
  // /api/schedule/last only stores published (feasible) solves today; the
  // status field is read defensively in case the payload ever carries one.
  const infeasible = lastSolve.data?.status === "INFEASIBLE";
  const healthy = Boolean(plan) && !infeasible;
  // Unknown until the unit is set and the first commit fetch has answered.
  const healthKnown =
    Boolean(unit) && (!committed.loading || committed.data !== null);

  // Counts follow the app's "plan in force" rule (reports.current_plan):
  // the committed plan when one exists, otherwise the last published solve.
  const schedule = plan?.schedule ?? lastSolve.data?.schedule ?? null;
  const ops = schedule?.length ?? null;
  const batches = schedule
    ? new Set(schedule.map((e) => e.work_order_id)).size
    : null;

  const sap = integ.data?.sap ?? null;
  const ebmr = integ.data?.ebmr ?? null;

  const muted = { color: "var(--text-muted)" };

  /**
   * What a connector's state may HONESTLY be called.
   *
   * "Connected" is a claim about a remote answering, and nothing here checks
   * that. `configured` is true as soon as a base URL string has been saved —
   * and eBMR's default base_url is a real-looking address, so the bar showed
   * a green "Connected" on a system that had never contacted anything.
   *
   * The mode is what separates the two: on "simulated" the connector runs
   * against a built-in stub with no remote at all, so it is reported as
   * Simulated, in muted grey. Live-and-configured is the only state that
   * earns "Configured" and a green dot — and even that says the settings are
   * complete, not that the last call succeeded, which is the Integration
   * Status page's job to show.
   */
  const connector = (label, cfg, error) => {
    if (error) return { text: `${label}: Error`, dot: "var(--bad)" };
    if (!cfg) return { text: `${label}: —`, dot: "var(--text-muted)" };
    if (cfg.mode === "simulated")
      return { text: `${label}: Simulated`, dot: "var(--text-muted)", muted: true };
    return cfg.configured
      ? { text: `${label}: Configured`, dot: "var(--good)" }
      : { text: `${label}: Not configured`, dot: "var(--text-muted)", muted: true };
  };
  const sapState = connector("SAP", sap, integ.error);
  const ebmrState = connector("eBMR", ebmr, integ.error);

  return (
    <div
      className="sticky z-10 flex w-full items-center gap-2 overflow-hidden px-6 text-xs"
      style={{
        top: 64, // sits directly under the 64px header, always visible
        height: 30,
        flexShrink: 0,
        background: "var(--page)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      {/* 1 — schedule health, derived: a plan is committed AND the last
             solve wasn't infeasible. Unknown reads as "—", not as healthy. */}
      <Segment to="/planner" title="Open the Planning Workbench" className="flex">
        {!healthKnown || committed.error ? (
          <span style={muted}>Schedule: —</span>
        ) : healthy ? (
          <>
            {glyph(CircleCheck, "var(--good)")}
            <span>Schedule: Healthy</span>
          </>
        ) : (
          <>
            {glyph(TriangleAlert, "var(--warn)")}
            <span>Schedule: Attention</span>
          </>
        )}
      </Segment>

      <Sep className="flex" />

      {/* 2 — commit state; "Uncommitted changes" only when the last solve is
             provably newer than the plan of record (Line Plan's comparison). */}
      <Segment to="/planner" title="Open the Planning Workbench" className="flex">
        {!unit || committed.error ? (
          <span style={muted}>—</span>
        ) : uncommitted ? (
          <>
            <Dot color="var(--warn)" />
            <span style={{ color: "var(--warn)" }}>Uncommitted changes</span>
          </>
        ) : plan ? (
          <>
            {glyph(Check, "var(--good)")}
            <span>Committed</span>
          </>
        ) : committed.loading ? (
          <span style={muted}>—</span>
        ) : (
          <span style={muted}>No committed plan</span>
        )}
      </Segment>

      <Sep className="hidden sm:flex" />

      {/* 3 — size of the plan in force. */}
      <Segment
        to="/orders"
        title="Open Production Orders"
        className="hidden sm:flex"
      >
        {glyph(ClipboardList)}
        {ops == null ? (
          <span style={muted}>— Operations · — Batches</span>
        ) : (
          <span>
            {ops} Operation{ops === 1 ? "" : "s"} · {batches} Batch
            {batches === 1 ? "" : "es"}
          </span>
        )}
      </Segment>

      <Sep className="hidden md:flex" />

      {/* 4 — last optimization, relative; "—" until the server reports a
             solve timestamp for this unit. */}
      <Segment
        to="/planner"
        title="Open the Planning Workbench"
        className="hidden md:flex"
      >
        {glyph(Cog)}
        <span style={solvedAt ? undefined : muted}>
          Last optimization: {solvedAt ? relative(solvedAt, nowMs) ?? "—" : "—"}
        </span>
      </Segment>

      <span className="ml-auto" />

      {/* 5 — connectors, from the same status endpoint the Integration
             Status page reads. Green only when the config says configured. */}
      <Segment
        to="/integrations/sap"
        title="Open SAP / ERP integration settings"
        className="hidden lg:flex"
      >
        {glyph(Network)}
        <Dot color={sapState.dot} />
        <span style={sapState.muted ? muted : undefined}>{sapState.text}</span>
      </Segment>

      <Sep className="hidden lg:flex" />

      <Segment
        to="/integrations/ebmr"
        title="Open eBMR integration settings"
        className="hidden lg:flex"
      >
        {glyph(Plug)}
        <Dot color={ebmrState.dot} />
        <span style={ebmrState.muted ? muted : undefined}>
          {ebmrState.text}
        </span>
      </Segment>
    </div>
  );
}
